import { format, parseISO, isValid } from 'date-fns';

/**
 * Formats a date string, number, or Date object into a standardized application format.
 * 
 * @param date - The date to format (Date | string | number | null | undefined)
 * @param withTime - Whether to include the time in the output
 * @returns Formatted date string (e.g., "Jan 29, 2026" or "Jan 29, 2026   11:15 AM")
 */
export const formatDate = (
  date: Date | string | number | null | undefined,
  withTime = false
): string => {
  if (!date) return '-';

  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    // Try parsing as ISO
    dateObj = parseISO(date);
    
    // Fallback for non-ISO strings (like "10/25/2025 10:21:08 AM")
    if (!isValid(dateObj)) {
      dateObj = new Date(date);
    }
  }

  if (!isValid(dateObj)) return '-';

  // Format: "Jan 29, 2026" or "Jan 29, 2026   11:15 AM"
  const formatStr = withTime ? 'MMM d, yyyy hh:mm a' : 'MMM d, yyyy';
  return format(dateObj, formatStr);
};

/**
 * Finds date patterns (MM/DD/YYYY or MM/DD/YYYY H:MM AM/PM) in text and replaces them
 * with the app's standard format (MMM d, yyyy or MMM d, yyyy hh:mm a).
 * Used for API text that contains dates in non-standard format (e.g. Analysis data).
 */
export const formatDatesInText = (text: string): string => {
  if (!text || typeof text !== 'string') return text;
  const pattern = /\b(\d{1,2}\/\d{1,2}\/\d{4}(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM))?)\b/gi;
  return text.replace(pattern, (match) => {
    const withTime = /\d{1,2}:\d{2}/.test(match) && /AM|PM/i.test(match);
    const formatted = formatDate(match.trim(), withTime);
    return formatted !== '-' ? formatted : match;
  });
};
